// Sky & Viewport constants (docs/02-Game-Design/Sky & Viewport.md). Starting
// values, to be verified on real devices; every number is in sky units.

export const SKY_HEIGHT = 1000;

export const SKY_PRESETS = ['1:1', '4:3', '16:9', '2:1', '21:9', '3:1', '4:1', '5:1', '6:1'] as const;
export type SkyPreset = (typeof SKY_PRESETS)[number];

const PRESET_WIDTHS: Record<SkyPreset, number> = {
  '1:1': 1000,
  '4:3': 1333,
  '16:9': 1778,
  '2:1': 2000,
  '21:9': 2333,
  '3:1': 3000,
  '4:1': 4000,
  '5:1': 5000,
  '6:1': 6000,
};

export function skyWidth(preset: SkyPreset): number {
  return PRESET_WIDTHS[preset];
}

/** Star glyph diameter. */
export const GLYPH_UNITS = 100;
/**
 * Overlap radius: a line closer than this to a star's centre passes over it
 * and blocks it. 35 (Aaron, 2026-11-04; 50 until then) is the drawn disc on a
 * tablet or desktop plus half a line, so a line blocks a star there only when
 * it touches it; a phone draws the disc larger, so a line may graze it there
 * without blocking it, which players mind less than the reverse.
 */
export const R_BLOCK = 35;
/** Minimum center-to-center node spacing (a generation constraint). */
export const NODE_SPACING = 160;
/**
 * No star centre closer than this to the sky's boundary: its disc, glow and
 * point value stay inside the sky (the schema checks it). Keeping stars clear
 * of the HUD is the screen's job, in pixels (game/src/display/sky/viewport.ts).
 */
export const STAR_EDGE = 65;
/** Constellation line width. */
export const LINE_WIDTH = 6;
/** The lit disc is this fraction of the glyph. */
export const DISC_FRACTION = 0.78;

export const SKY_ARTS = ['default'] as const;
export type SkyArt = (typeof SKY_ARTS)[number];

/**
 * Apogee's planet (docs/02-Game-Design/Core Mechanics.md, Planets). It must be
 * ringable, so its centre keeps well clear of the sky's edge and of every star.
 */
/** No planet centre closer than this to the sky's boundary (350 until Aaron's 200 of 2026-11-03). */
export const PLANET_MARGIN = 200;
/** Minimum centre-to-centre distance from a planet to any star or planet. */
export const PLANET_SPACING = NODE_SPACING;
/** The planets a sky may show, in order from the Sun; no kind twice on one sky (Aaron, 2026-11-03). */
export const PLANET_KINDS = ['mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'] as const;
export type PlanetKind = (typeof PLANET_KINDS)[number];
/**
 * A planet's widest extent: each planet is scaled so its shape is this wide
 * (or tall) and drawn smaller on large screens like a star glyph (Aaron,
 * 2026-11-04; the diamond round it is gone).
 */
export const PLANET_UNITS = 64;
/**
 * Overlap radius for a planet: a line closer than this to its centre passes
 * over it and blocks it, so a loop rings it only when every line of the loop
 * stays this far out (Aaron, 2026-11-04). It is about the planet as a tablet
 * or desktop draws it (64 × 0.72 / 2, plus half a line).
 */
export const R_BLOCK_PLANET = 25;
